import { BarChart as BChart } from "@mui/x-charts/BarChart";
import { FormGetField, FormsGetResponse } from "../../models/Form";
import dayjs, { Dayjs } from "dayjs";
import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import React from "react";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";

interface Props<T extends FormsGetResponse> {
  items: T[];
  xAxisKey: string;
  valueKey: string;
}

const BarChart = <T extends FormsGetResponse>({
  items,
  xAxisKey,
  valueKey,
}: Props<T>) => {
  const currentYear = dayjs().year();
  const startYear = currentYear - 10;
  const [selectedYear, setSelectedYear] = React.useState<string>(
    currentYear.toString()
  );
  const yearOptions = Array.from({ length: 11 }, (_, i) =>
    (startYear + i).toString()
  ).sort((a, b) => parseInt(b, 10) - parseInt(a, 10));

  const getValuesPerMonth = (items: any[], valueKey: string) => {
    const valuesPerMonth: { date: Dayjs; value: number }[] = [];

    items.forEach((item) => {
      const value = item.values.find(
        (b: FormGetField) => b.fieldAlias === valueKey
      )?.value;
      const dateValue = item.values.find(
        (a: FormGetField) => a.fieldAlias === xAxisKey
      )?.value;
      if (value) {
        const date = dayjs(dateValue);
        if (!valuesPerMonth.find((a) => a.date.isSame(date, "month"))) {
          valuesPerMonth.push({
            date: date,
            value: parseInt(value),
          });
        } else {
          valuesPerMonth.map((a) => {
            if (a.date.isSame(date, "month")) {
              a.value = a.value + parseInt(value);
            }
            return a;
          });
        }
      }
    });
    const sortedMonths = valuesPerMonth
      .filter(
        (entry) => dayjs(entry.date).year() === dayjs(selectedYear).year()
      )
      .sort((a, b) => {
        const dateA = dayjs(a.date);
        const dateB = dayjs(b.date);
        return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
      });
    return sortedMonths;
  };

  const numberOfDaysPerMonth = getValuesPerMonth(items, valueKey);

  return (
    <Box>
      <InputLabel>År</InputLabel>
      <FormControl sx={{ margin: 2 }}>
        <Select
          value={selectedYear}
          style={{ maxWidth: "170.99px" }}
          onChange={(e) => {
            e.preventDefault();
            setSelectedYear(e.target.value);
          }}
          IconComponent={(e) => (
            <ArrowDropDownIcon cursor="pointer" className={e.className} />
          )}
          data-test="eventlibrary-new-list-color-select"
        >
          {yearOptions.map((option) => {
            return (
              <MenuItem
                key={option}
                value={option}
                sx={{
                  "&:hover": {
                    fontWeight: "700",
                    borderWidth: 3,
                    borderColor: "black",
                  },
                }}
                data-test="event-library-colors-menu-item"
              >
                {option}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      <BChart
        series={[
          {
            data: numberOfDaysPerMonth.map((a) => a.value),
          },
        ]}
        height={290}
        xAxis={[
          {
            data: numberOfDaysPerMonth.map((a) => a.date.format("YYYY-MM")),
            scaleType: "band",
          },
        ]}
        yAxis={[
          {
            min: 0,
          },
        ]}
        margin={{ top: 10, bottom: 30, left: 40, right: 10 }}
      />
    </Box>
  );
};

export default BarChart;
